import axios from 'axios';
import { Tokens } from '../models/authentication.interface';
import { CartItem, Cart } from '../models/cart.interface';
import { LoginResponse } from '../models/authentication.interface';
import { Order, OrderItem } from '../models/order.interface';
import { SearchType } from '../models/search.interface';
import { User } from '../models/user.interface';
import { Company, Location } from '../models/location.interface';
import { SniffKeyResponse } from '../models/sniffKey.interface';
import { Matcher, MatcherGroup, RulesType } from '../models/matcher.interface';

const instance = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:5000'}`
});

instance.interceptors.request.use(
  request => {
    request.headers.post['Content-Type'] = 'application/json';

    if(localStorage.getItem('refreshToken')){
      request.headers.common['Authorization'] = localStorage.getItem('refreshToken');
    }

    return request 
  }, 
  error => {
    return Promise.reject(error)
  }
)

instance.interceptors.response.use(response => response.data)

export const Authentication = {
  getAccessToken: (refreshToken: string)
    : Promise<Tokens> => instance.post(`/auth/access-token`, 
      { refreshToken: refreshToken }),
  login: (userName: string, password: string)
    : Promise<LoginResponse> => instance.post('/auth/login', 
      { 
        userName: userName, 
        password: password 
      }),
  resetPassword: (token: string, password: string)
    : Promise<LoginResponse> => instance.post(`/auth/reset-password`, 
      { 
        token: token, 
        password: password 
      }),
  rememberUserName: (email: string)
    : Promise<string> => instance.post(`/auth/remember-user-name`, 
      { email: email }),
  emailPasswordReset: (userName: string)
    : Promise<string> => instance.post(`/auth/email-password-reset`, 
    { userName: userName }),
}

export const Carts = {
  getSelfCarts: (locationId: string) 
    : Promise<Cart[]> => instance.get(`/self/carts`,
      {data:{ locationId: locationId }}),
  getSelfCartsById: (cartId: string)
    : Promise<Cart> => instance.get(`/self/carts/${cartId}`),
  getCartItems: (cartId: string)
    : Promise<CartItem[]> => instance.get(`/self/carts/${cartId}/items`),
  addCartItem: (cartId: string, searchId: string, quantity: number)
    : Promise<CartItem> => instance.post(`/self/carts/${cartId}/items`,
      { 
        searchId: searchId,
        quantity: quantity
      }),
  getCartItem: (cartId: string, itemId: string)
    : Promise<CartItem> => instance.get(`/self/carts/${cartId}/items/${itemId}`),
  updateCartItem: (cartId: string, itemId: string, quantity: number, lotNumber?: string, sellerName?: string)
    : Promise<CartItem> => instance.patch(`/self/carts/${cartId}/items/${itemId}`,
      {
        quantity: quantity,
        lotNumber: lotNumber,
        sellerName: sellerName
      }),
  deleteCartItem: (cartId: string, itemId: string): Promise<string> => instance.delete(`/self/carts/${cartId}/items/${itemId}`),
}

export const Orders = {
  getSelfOrders: (locationId?: string, searchId?: string)
    : Promise<Order[]> => instance.get(`/self/orders`,
      { data: {
        locationId: locationId,
        searchId: searchId}
      }),
  getOrder: (orderId: string): Promise<Order> => instance.get(`/self/orders/${orderId}`),
  createOrder: (cartId: string): Promise<Order> => instance.post(`/self/orders`,
    { cartId: cartId }),
  getOrderItems: (orderId: string): Promise<OrderItem[]> => instance.get(`/self/orders/${orderId}/items`),
  getOrderItem: (orderId: string, itemId: string): Promise<OrderItem> => instance.get(`/self/orders/${orderId}/items/${itemId}`)
}

export const Searches = { 
  getSearches: (locationId: string)
    : Promise<SearchType[]> => instance.get(`/self/searches`, 
      { data: {
        locationId: locationId
      }}),
  createSearch: (vin: string, locationId: string, lotNumber?: string, sellerName?: string)
    : Promise<SearchType> => instance.post(`/self/searches`,
      {
        vin: vin,
        locationId: locationId,
        lotNumber: lotNumber,
        sellerName: sellerName
      }),
  getSearch: (searchId: string): Promise<SearchType> => instance.get(`/self/searches/${searchId}`),
  updateSearch: (searchId: string, lotNumber?: string, sellerName?: string)
    : Promise<SearchType> => instance.patch(`/self/searches/${searchId}`,
      {
        lotNumber: lotNumber,
        sellerName: sellerName
      }),
}

export const Users = { 
  getSelf: (): Promise<User> => instance.get(`/self`),
  getAllUsers: (): Promise<User[]> => instance.get(`/users`),
  createUser: (userName: string, userType: string, firstName: string, lastName: string, email: string, password: string)
    : Promise<User> => instance.post(`/users`,
      {
        userName: userName,
        userType: userType,
        firstName: firstName,
        lastName: lastName,
        email: email,
        password: password
      }),
  getUser: (userId: string): Promise<User> => instance.get(`/users/${userId}`),
  updateUser: (userId: string, userName: string, userType: string, firstName: string, lastName: string, email: string, enable: boolean)
    : Promise<User> => instance.patch(`/users/${userId}`,
      {
        userName: userName,
        userType: userType,
        firstName: firstName,
        lastName: lastName,
        email: email,
        enabled: enable
      })
}

export const Locations = {
  getSelfLocations: (): Promise<Location[]> => instance.get(`/self/locations`),
  getLocations: (userId: string): Promise<Location[]> => instance.get(`/users/${userId}/locations`),
  addLocation: (userId: string, locationId: string)
    : Promise<Location> => instance.post(`/users/${userId}/locations`,
      {
        locationId: locationId
      }),
  getUserLocation: (userId: string, locationId: string)
    : Promise<Location> => instance.get(`/users/${userId}/locations/${locationId}`),
  deleteUserLocation: (userId: string, locationId: string)
    : Promise<string> => instance.delete(`/users/${userId}/locations/${locationId}`),
  getAllLocations: (): Promise<Location[]> => instance.get(`/locations`),
  createLocation: (companyId: string, name: string, phoneNumber: string)
    : Promise<Location> => instance.post(`/locations`, 
    {
      companyId: companyId,
      name: name,
      phoneNumber: phoneNumber
    }),
  getLocation: (locationId: string): Promise<Location> => instance.get(`/locations/${locationId}`),
  updateLocation: (locationId: string, companyId: string, name: string, phoneNumber: string)
    : Promise<Location> => instance.patch(`locations/${locationId}`,
    {
      companyId: companyId,
      name: name,
      phoneNumber: phoneNumber
    })
}

export const Companies = {
  getAllCompanies: (): Promise<Company[]> => instance.get(`/companies`),
  getCompany: (companyId: string): Promise<Company> => instance.get(`/companies/${companyId}`)
}

export const SniffKey = {
  sniffKey: (vin: string)
    : Promise<SniffKeyResponse> => instance.get(`/api/sniff-key/${vin}`)
}

export const MatcherGroups = {
  getAllMatcherGroups: (): Promise<MatcherGroup[]> => instance.get(`/api/matcher-groups`),
  createMatcherGroup: (allRequired: boolean, keyType: string, price: number)
    : Promise<MatcherGroup> => instance.post(`/api/matcher-groups`,
      {
        allRequired: allRequired,
        keyType: keyType,
        price: price
      }),
  getMatcherGroup: (groupId: string): Promise<MatcherGroup> => instance.get(`/api/matcher-groups/${groupId}`),
  updateMatcherGroup: (groupId: string, allRequired: boolean, keyType: string, price: number)
    : Promise<MatcherGroup> => instance.patch(`/api/matcher-groups/${groupId}`,{
            allRequired: allRequired,
            keyType: keyType,
            price: price
    }),
  deleteMatcherGroup: (groupId: string): Promise<string> => instance.delete(`/api/matcher-groups/${groupId}`),
}

export const Matchers = {
  getAllMatchers: (groupId: string): Promise<Matcher[]> => instance.get(`/api/matcher-groups/${groupId}/matchers`),
  createMatcher: (groupId: string, type: string, operator: string, value: string)
    : Promise<Matcher> => instance.post(`/api/matcher-groups/${groupId}/matchers`,
      {
        type: type,
        operator: operator,
        value: value
      }),
  getMatcher: (groupId: string, matcherId: string)
    : Promise<Matcher> => instance.get(`/api/matcher-groups/${groupId}/matchers/${matcherId}`),
  updateMatcher: (groupId: string, matcher: Matcher)
    : Promise<Matcher> => instance.patch(`/api/matcher-groups/${groupId}/matchers/${matcher.id}`,
      {
        type: matcher.type,
        operator: matcher.operator,
        value: matcher.value
      }),
  deleteMatcher: (groupId: string, matcherId: string)
    : Promise<string> => instance.delete(`/api/matcher-groups/${groupId}/matchers/${matcherId}`)
}

export const parseRulesResponse = (matcherGroup: MatcherGroup): RulesType =>{
  const make = matcherGroup.matchers.find(m => m.type === 'MAKE')
  const model = matcherGroup.matchers.find(m => m.type === 'MODEL')
  const years = matcherGroup.matchers.filter(m => m.type === 'YEAR')
  const company = matcherGroup.matchers.filter(m => m.type === 'COMPANY')

  return {
    id: matcherGroup.id,
    keyType: matcherGroup.keyType,
    price: matcherGroup.price,
    make: make?.value || '',
    makeOperator: (make?.operator as StringOperator) || 'EQUALS',
    model: model?.value || '',
    modelOperator: (model?.operator as StringOperator) || 'EQUALS',
    minYear: years.find(y => y.operator === 'GREATER_THAN_OR_EQUALS')?.value || '',
    maxYear: years.find(y => y.operator === 'LESS_THAN_OR_EQUALS')?.value || '',
    company: company.find(y => y.operator === 'EQUALS')?.value || 'Any',
  }
}
